import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { useDialog } from '@/components/dialogs/useDialog';
import { ConfirmModal } from '@/components/dialogs/ConfirmModal';
import { AddUserModal } from '@/components/dialogs/AddUserModal';
import {
  useUsersQuery,
  useToggleUserMutation,
} from '@/features/useraccount/api';
import type { InternalUser } from '@/types/api';

export function UserListPage() {
  const { t } = useTranslation();
  const { open: openDialog } = useDialog();
  const usersQuery = useUsersQuery();
  const toggleUser = useToggleUserMutation();
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const filtered = useMemo(() => {
    const users = usersQuery.data ?? [];
    if (!filter.trim()) return users;
    const term = filter.toLowerCase();
    return users.filter(
      (u) =>
        u.username?.toLowerCase().includes(term) ||
        u.firstName?.toLowerCase().includes(term) ||
        u.lastName?.toLowerCase().includes(term),
    );
  }, [usersQuery.data, filter]);

  const paged = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleAddUser = async () => {
    const created = await openDialog<boolean | null>((close) => (
      <AddUserModal onClose={close} />
    ));
    if (created) {
      void usersQuery.refetch();
    }
  };

  const handleToggle = async (user: InternalUser, block: boolean) => {
    const confirmed = await openDialog<boolean | null>((close) => (
      <ConfirmModal onClose={close} />
    ));
    if (!confirmed) return;
    const res = await toggleUser.mutateAsync({ userId: user.userId, block });
    if (res.code !== 0) {
      await Swal.fire(t('messages.error', { defaultValue: 'Error' }), res.message, 'error');
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('menus.menuUserList', { defaultValue: 'Users' })}
      </Typography>
      <Paper sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ mb: 2 }}
        >
          <TextField
            size="small"
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setPage(0);
            }}
            placeholder={t('pages.user-list.filter', { defaultValue: 'Filter users…' })}
            sx={{ minWidth: 260 }}
          />
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddUser}>
            {t('pages.user-list.add-user', { defaultValue: 'Add user' })}
          </Button>
        </Stack>

        {usersQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('pages.user-list.table.header.info', { defaultValue: 'Info' })}</TableCell>
                    <TableCell align="center">
                      {t('pages.user-list.table.header.username', { defaultValue: 'Username' })}
                    </TableCell>
                    <TableCell align="center">
                      {t('pages.user-list.table.header.job', { defaultValue: 'Profile' })}
                    </TableCell>
                    <TableCell align="center">
                      {t('pages.user-list.table.header.status', { defaultValue: 'Status' })}
                    </TableCell>
                    <TableCell align="center">
                      {t('pages.user-list.table.header.actions', { defaultValue: 'Actions' })}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((user) => {
                    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
                    return (
                      <TableRow key={user.userId} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar src={user.picture} alt={fullName} />
                            <Box>
                              <Tooltip title={fullName.length > 20 ? fullName : ''}>
                                <Typography variant="body2" fontWeight={500}>
                                  {fullName.length > 20
                                    ? `${fullName.slice(0, 20)}…`
                                    : fullName}
                                </Typography>
                              </Tooltip>
                              <Typography variant="caption" color="text.secondary">
                                {user.id}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell align="center">{user.username}</TableCell>
                        <TableCell align="center">{user.profile}</TableCell>
                        <TableCell align="center">
                          {user.blocked ? (
                            <Chip
                              label={t('pages.user-list.blocked', { defaultValue: 'Blocked' })}
                              color="error"
                              size="small"
                            />
                          ) : (
                            <Chip
                              label={t('pages.user-list.active', { defaultValue: 'Active' })}
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {user.blocked ? (
                            <Tooltip title={t('pages.user-list.unblock', { defaultValue: 'Unblock' })}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleToggle(user, false)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title={t('pages.user-list.block', { defaultValue: 'Block' })}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleToggle(user, true)}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paged.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('messages.no-data', { defaultValue: 'No data' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              labelDisplayedRows={({ from, to, count }) =>
                t('pages.userOperations.table.pagination.range', {
                  defaultValue: `${from}-${to} of ${count}`,
                  from,
                  to,
                  count,
                })
              }
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
